/**
 * Where an insured reads and signs a broker-of-record letter.
 *
 * ⚠️ THE ONLY PAGE IN THE HUB WITH NO SIGNED-IN USER. The person here is a
 * restaurant owner who got an email, not a member — asking them to make an
 * account before they can appoint their own agent is how a transfer stalls. The
 * token in the URL is the whole authorisation, which is why the server answers
 * 404 to anything it does not recognise and never says why.
 *
 * ⚠️ AND IT IS DELIBERATELY PLAIN. Every other hub page is a product surface
 * selling its own capability; this one is a document and a decision. A person
 * signing something that changes who controls their insurance should see the
 * letter, not our navigation.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

interface SignPayload {
  id: string;
  kind: 'bor' | 'aor';
  insuredName: string;
  signerName: string;
  signerTitle: string;
  agencyName: string;
  alreadySigned: boolean;
  letters: { carrier: string; text: string }[];
  affirmations: { identity: string; authority: string };
  affirmationVersion: string;
}

const BASE = import.meta.env.VITE_API_BASE_URL || '';

export function BorSign(): JSX.Element {
  const { token = '' } = useParams();

  const [data, setData] = useState<SignPayload | null>(null);
  const [missing, setMissing] = useState(false);
  const [loading, setLoading] = useState(true);

  const [typedName, setTypedName] = useState('');
  const [identity, setIdentity] = useState(false);
  const [authority, setAuthority] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    fetch(`${BASE}/api/public/bor/${encodeURIComponent(token)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('gone'))))
      .then((d: SignPayload) => {
        if (!live) return;
        setData(d);
        // Their own name, pre-filled: this is a signature, not a memory test.
        setTypedName(d.signerName ?? '');
      })
      .catch(() => live && setMissing(true))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [token]);

  const what = data?.kind === 'aor' ? 'Agent of Record' : 'Broker of Record';

  async function sign() {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${BASE}/api/public/bor/${encodeURIComponent(token)}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          typedName: typedName.trim(),
          affirmedIdentity: identity,
          affirmedAuthority: authority,
        }),
      });
      const body = (await res.json()) as { error?: string };
      if (!res.ok) {
        setError(body.error ?? 'That did not go through. Try again in a moment.');
        return;
      }
      setDone(true);
    } catch {
      setError('That did not go through. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="sg-wrap">
        <p className="sg-quiet">Loading…</p>
      </div>
    );
  }

  /*
    ⚠️ ONE MESSAGE FOR EVERY REASON A LINK DOES NOT WORK. Expired, withdrawn,
    mistyped and never-existed all land here, because telling a stranger which
    one it is confirms whether a request exists. The way out is a person, not a
    retry.
  */
  if (missing || !data) {
    return (
      <div className="sg-wrap">
        <div className="sg-card">
          <h1>This link is not available</h1>
          <p>
            It may have expired, or already been used. Please contact the agency that sent it to
            you and they can send a new one.
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="sg-wrap">
        <div className="sg-card">
          <h1>Signed — thank you</h1>
          {/*
            ⚠️ DO NOT TELL THEM IT HAS GONE TO THE CARRIER, BECAUSE IT HAS NOT.
            The agency checks the signature and sends it. Saying "sent" here
            would be the one sentence on this page that is untrue, and it is the
            sentence somebody would quote back if a carrier said they never
            received anything.
          */}
          <p>
            Your {what} letter is signed. {data.agencyName} will check it over and send it to{' '}
            {data.letters.length === 1 ? 'the carrier' : 'the carriers'} named in it. Nothing else
            is needed from you.
          </p>
          <p className="sg-quiet">
            Carriers usually action a change of record within a few working days of receiving it.
          </p>
        </div>
      </div>
    );
  }

  if (data.alreadySigned) {
    return (
      <div className="sg-wrap">
        <div className="sg-card">
          <h1>Already signed</h1>
          <p>
            This letter has already been signed, and {data.agencyName} is looking after the rest.
            There is nothing further to do — if you were expecting to sign something else, contact
            them.
          </p>
        </div>
      </div>
    );
  }

  const ready = typedName.trim().length >= 2 && identity && authority;

  return (
    <div className="sg-wrap">
      <div className="sg-head">
        <h1>
          {what} letter for {data.insuredName}
        </h1>
        <p>
          {data.agencyName} has prepared{' '}
          {data.letters.length === 1 ? 'this letter' : `these ${data.letters.length} letters`} for
          your signature.{' '}
          {data.letters.length > 1 && <>You sign once and all of them are sent.</>}
        </p>
      </div>

      {/*
        ⚠️ EVERY LETTER IS SHOWN IN FULL, NOT SUMMARISED. One signature covers
        all of them, so a person who has read one and signed for four has not
        consented to three of them. They are long and that is correct — this is
        the document, and the page exists to be read.
      */}
      {data.letters.map((l) => (
        <section className="sg-letter" key={l.carrier}>
          <h2>{l.carrier}</h2>
          <pre>{l.text}</pre>
        </section>
      ))}

      <div className="sg-card sg-sign">
        <h2>Sign</h2>

        <label htmlFor="sg-name">Type your full name</label>
        <input
          id="sg-name"
          type="text"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          autoComplete="name"
        />

        {/*
          ⚠️ TWO STATEMENTS, TWO BOXES, AND THEY ARE NOT INTERCHANGEABLE.
          Confirming your own name must never imply you are authorised to appoint
          an agent for a company. The server rejects anything but true for both,
          rather than storing a signature that records one of them as false.
        */}
        <label className="sg-affirm">
          <input type="checkbox" checked={identity} onChange={(e) => setIdentity(e.target.checked)} />
          <span>{data.affirmations.identity}</span>
        </label>

        <label className="sg-affirm">
          <input
            type="checkbox"
            checked={authority}
            onChange={(e) => setAuthority(e.target.checked)}
          />
          <span>{data.affirmations.authority}</span>
        </label>

        {error && <p className="sg-error">{error}</p>}

        <button type="button" className="btn btn-primary" disabled={!ready || busy} onClick={sign}>
          {busy ? 'Signing…' : `Sign ${data.letters.length === 1 ? 'this letter' : 'these letters'}`}
        </button>

        <p className="sg-quiet">
          Signing authorises the {data.letters.length === 1 ? 'letter' : 'letters'} above. This
          appoints who represents you — it does not change or cancel your insurance.
        </p>
      </div>
    </div>
  );
}
